/**
 * Modal to set a dashboard alert from an insight (Set Alert / Plan Buffer).
 */
import React from 'react';
import { Bell, X } from 'lucide-react';

const INSIGHT_ALERTS_KEY = 'insights-active-alerts';

export interface InsightAlertContext {
  insightId: string;
  title: string;
  category?: string;
  action?: string;
  frequency?: string;
}

export function loadActiveAlerts(): InsightAlertContext[] {
  try {
    const raw = sessionStorage.getItem(INSIGHT_ALERTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as InsightAlertContext[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveActiveAlert(alert: InsightAlertContext): void {
  const existing = loadActiveAlerts();
  const withoutCurrent = existing.filter((a) => a.insightId !== alert.insightId);
  sessionStorage.setItem(INSIGHT_ALERTS_KEY, JSON.stringify([...withoutCurrent, alert]));
}

interface InsightAlertModalProps {
  open: boolean;
  context: InsightAlertContext | null;
  onClose: () => void;
  onSaved?: (alert: InsightAlertContext) => void;
}

export function InsightAlertModal({
  open,
  context,
  onClose,
  onSaved,
}: InsightAlertModalProps): React.ReactElement | null {
  const [frequency, setFrequency] = React.useState('weekly');
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setSaved(false);
      setFrequency(context?.frequency ?? 'weekly');
    }
  }, [open, context?.insightId, context?.frequency]);

  if (!open || !context) return null;

  const isBuffer = context.action === 'Plan Buffer';
  const heading = isBuffer ? 'Plan Income Buffer' : 'Set Smart Alert';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const savedAlert: InsightAlertContext = {
      ...context,
      frequency,
    };
    saveActiveAlert(savedAlert);
    setSaved(true);
    onSaved?.(savedAlert);
    setTimeout(onClose, 1200);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="insight-alert-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden />
            <h2 id="insight-alert-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {heading}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus-ring"
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
              {isBuffer ? 'Build a buffer based on this insight' : 'Create alert based on this insight'}
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-300 mt-1">{context.title}</p>
            {context.category && (
              <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">{context.category}</p>
            )}
          </div>

          <div>
            <label htmlFor="alert-frequency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Check frequency
            </label>
            <select
              id="alert-frequency"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus-ring"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            {isBuffer
              ? 'We will remind you to review your emergency fund target on the dashboard.'
              : 'You will receive a dashboard notification when this pattern is detected again.'}
          </p>

          {saved ? (
            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400" role="status">
              Alert saved — you will see reminders on your dashboard.
            </p>
          ) : (
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary btn-sm">
                Cancel
              </button>
              <button type="submit" className="btn-primary btn-sm inline-flex items-center gap-1.5">
                <Bell className="h-4 w-4" aria-hidden />
                {isBuffer ? 'Save plan' : 'Create alert'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export function isInsightAlertActive(insightId: string): boolean {
  return loadActiveAlerts().some((a) => a.insightId === insightId);
}
